import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor } from './lib/vdjLook.js';
import { getFountain } from '../fountain-assets.js';

// Contrat du rig partagé : interdit. La lumière vient des projecteurs
// sous-marins des buses.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'fountain',
  family: 'photographic',
  danceability: 0.80,
  requiresTier: 0,
  energyLevel: 0.72,
  bpmMin: 80,
  bpmMax: 140,
  movement: 0.70,
  tonality: 0.80,
  enabled: true
};

// FOUNTAIN : une fontaine musicale de nuit sur un lac en ville. Une rangée de
// buses en perspective sur le lac de la photo (tools/build-fountain.mjs :
// masques ciel / lac / balustrade, horizon mesuré sur la profondeur, ligne
// des buses) ; des jets éclairés par en dessous dansent en figures calées sur
// la musique. Pop, ballades énergiques, électro mélodique, classique épique,
// musiques de film.
//
// LE PLAN DU LAC : une caméra de niveau, horizon y_h, focale F, hauteur 1 :
// y = y_h + F (1 - Y) / Z, x = cx + F X / Z (pixels de la photo). Les jets
// vivent dans ce monde : ils rapetissent au loin d'eux-mêmes.
//
// LES JETS : chaque goutte est calculée dans le vertex shader depuis son
// INSTANT DE LANCEMENT (âge = fract(t / L + graine) x L) : la commande de sa
// buse à cet instant (ouverte ou non, hauteur, type) et, pour une rame,
// l'angle qu'avait la buse - si bien qu'un oarsman qui se balance dessine un
// vrai jet courbe, comme une lance qu'on agite. Balistique : g est tel que
// le plus haut jet monte en 0,3 s ; passé l'apex, la goutte s'écarte : la
// pluie. Particules de 1 px (R7). Le CORPS du jet est un ruban qui suit la
// même fonction (âges 0 à 0,6 s), densité gaussienne en travers, additif,
// lumineux près de la buse et qui s'éteint en hauteur (R14 : jamais
// d'aplat). Rideau bas (particules basses et larges), brume (sprites doux,
// tier >= 1), halo du projecteur sur l'eau.
//
// LE LAC : un champ de rides 256 x 128 dans le plan (X, Z), équation des
// ondes en ping-pong, nourri là où l'eau retombe (pied d'un shooter, point de
// chute d'une rame) ; il déplace le reflet de la ville et coupe le reflet
// des jets (miroir vertical flou autour de la ligne des buses). La
// balustrade (masque) passe devant tout, jets et reflets compris. Les jets
// sont atténués près de la pochette (plancher 0,5) et ne montent pas dans
// la bande des 12 % du haut (le nom de la station, les boutons).
//
// LA CHORÉGRAPHIE : une figure par phrase, choisie par l'énergie (BREATH
// calme ; SWAY / CROSS moyen ; WAVE / PULSE intense). WAVE : un shooter par
// croche, de gauche à droite. SWAY : tous les oarsmen en éventail, un
// aller-retour par mesure. CROSS : oarsmen croisés en X. PULSE : tous les
// shooters sur les temps forts. BREATH : rideau et brume seuls. Mesure :
// couleur des projecteurs. Montée : plus haut, plus de brume. Drop : tous les
// shooters au maximum, flash blanc, pluie générale sur 2 temps. Sans BPM :
// horloge interne à 100.
//
// DEBUG : ?fountainfig=wave|sway|cross|pulse|breath, ?fountainmask=1 (les
// masques), ?fountainprobe=1 (window.__fountain).

const NJ = 40;
const FIGS = ['breath', 'sway', 'cross', 'wave', 'pulse'];
const RISE_S = 0.30;            // the highest jet reaches its top in 0.3 s
const LIFE_S = 0.80;            // a drop's life (rise, fall, rain)
const TOP_BAND = 0.12 + 0.03;   // no jet in the top 12 % (+ margin)
const SAFE_FLOOR = 0.5;
const RIP = { w: 256, h: 128, X0: -14, X1: 14, Z0: 4.5, Z1: 22 };
const NO_BPM = 100;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const WARM = new THREE.Color(1.0, 0.82, 0.55);

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- GLSL ---------------------------------------------------------------------------
// the jets' shared vertex code: the plane, the commands, the trajectory
const JET_GLSL = `
  uniform vec4 uJetA[${NJ}];    // the current command: t_on, t_off, H, kind (0 shooter, 1 oarsman)
  uniform vec4 uJetB[${NJ}];    // the previous one
  uniform vec4 uNoz[${NJ}];     // X, Z, colour slot, active (tier)
  uniform float uTime;
  uniform float uG;
  uniform vec4 uSway;           // amplitude (rad), period (s), t0, mode (0 fan, 1 cross)
  uniform float uFan;
  uniform vec4 uMap;            // sx, sy, cy (image uv centre), -
  uniform vec2 uImg;
  uniform float uYH;
  uniform float uF;
  uniform float uAspect;
  uniform vec3 uCol[3];
  uniform float uColShift;
  uniform float uWarmMix;
  uniform float uFlash;
  vec2 toScreenUv(vec2 ipx) {
    vec2 img = ipx / uImg;
    return vec2((img.x - 0.5) / uMap.x + 0.5, 1.0 - ((img.y - uMap.z) / uMap.y + 0.5));
  }
  vec2 project(vec3 P) {
    float z = max(P.z, 0.5);
    return vec2(uImg.x * 0.5 + uF * P.x / z, uYH + uF * (1.0 - P.y) / z);
  }
  vec4 clipOf(vec3 P) { vec2 s = toScreenUv(project(P)); return vec4(s * 2.0 - 1.0, 0.0, 1.0); }
  bool cmdAt(int j, float tl, out vec4 c) {
    vec4 A = uJetA[j];
    c = tl >= A.x ? A : uJetB[j];
    return tl >= c.x && tl < c.y && c.z > 0.001;
  }
  float angleAt(int j, float tl, float kind) {
    if (kind < 0.5) return 0.0;
    float u = float(j) / ${(NJ - 1).toFixed(1)} * 2.0 - 1.0;
    float e = mod(float(j), 2.0) < 0.5 ? 1.0 : -1.0;
    float base = uSway.w < 0.5 ? u * uFan : e * uFan;
    float sgn = uSway.w < 0.5 ? 1.0 : e;
    return base + uSway.x * sin(6.2831853 * (tl - uSway.z) / uSway.y) * sgn;
  }
  // water launched at tl from nozzle j, 'a' seconds ago
  vec3 waterPos(int j, vec4 c, float tl, float a, vec3 seed, out float apex) {
    float v0 = sqrt(2.0 * uG * max(c.z, 0.0));
    float th = angleAt(j, tl, c.w) + (seed.x - 0.5) * 0.04;
    float vy = v0 * cos(th), vx = v0 * sin(th);
    apex = vy / uG;
    float post = max(a - apex, 0.0);
    vec4 N = uNoz[j];
    return vec3(N.x + vx * a + (seed.x - 0.5) * post * v0 * 0.35,
                vy * a - 0.5 * uG * a * a,
                N.y + (seed.y - 0.5) * post * v0 * 0.25);
  }
  // a GRADIENT along the row through the three palette colours (per-nozzle
  // alternation read as fairy lights); the bar turns it by a third
  vec3 spotColour(int j) {
    float k = mod(float(j) / ${(NJ / 3).toFixed(4)} + uColShift, 3.0);
    vec3 c = k < 1.0 ? mix(uCol[0], uCol[1], smoothstep(0.0, 1.0, k))
           : (k < 2.0 ? mix(uCol[1], uCol[2], smoothstep(1.0, 2.0, k)) : mix(uCol[2], uCol[0], smoothstep(2.0, 3.0, k)));
    c = mix(c, vec3(${WARM.r.toFixed(2)}, ${WARM.g.toFixed(2)}, ${WARM.b.toFixed(2)}), uWarmMix);
    return mix(c, vec3(1.0, 0.97, 0.92), uFlash);
  }
`;

// THE DROPS: the stream (kind 0) and the low curtain (kind 1), 1 px (R7)
const DROP_VERTEX = `
  precision highp float;
  ${JET_GLSL}
  attribute float aJet;
  attribute vec3 aSeed;
  attribute float aKind;
  uniform float uCurtain;
  uniform float uCurtainH;
  varying vec3 vC;
  void main() {
    int j = int(aJet + 0.5);
    vec4 N = uNoz[j];
    gl_PointSize = 1.0;
    if (N.w < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vC = vec3(0.0); return; }
    float L = ${LIFE_S.toFixed(2)};
    float a = fract(uTime / L + aSeed.z) * L;
    float tl = uTime - a;
    vec4 c;
    bool open;
    if (aKind > 0.5) {
      // the curtain: low, wide, always on at its level
      c = vec4(-1e9, 1e9, uCurtainH * (0.55 + 0.9 * aSeed.y), 0.0);
      open = uCurtain > 0.01;
    } else open = cmdAt(j, tl, c);
    float apex;
    vec3 P = open ? waterPos(j, c, tl, a, aSeed, apex) : vec3(0.0, -1.0, 1.0);
    if (aKind > 0.5) P.x += (aSeed.x - 0.5) * 1.1 * a;            // the curtain fans out
    if (!open || P.y < 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vC = vec3(0.0); return; }
    float lit = 0.15 + 0.85 * exp(-max(P.y, 0.0) / (0.45 * max(c.z, 0.05)));
    float g = aKind > 0.5 ? 0.30 * uCurtain : 0.55;
    vC = spotColour(j) * lit * g * (1.0 + 1.5 * uFlash);
    gl_Position = clipOf(P);
  }
`;
const DROP_FRAGMENT = `
  precision mediump float;
  varying vec3 vC;
  void main() { gl_FragColor = vec4(vC, 1.0); }
`;

// THE BODY: a ribbon along the same trajectory, ages 0 to 0.6 s, a gaussian
// density across (R14: translucent, additive, never a flat fill)
const RIBBON_VERTEX = `
  precision highp float;
  ${JET_GLSL}
  attribute float aJet;
  attribute float aU;
  attribute float aSide;
  uniform vec2 uPx;              // the target's size, px
  varying float vV;
  varying vec3 vC;
  void main() {
    int j = int(aJet + 0.5);
    vec4 N = uNoz[j];
    float a = aU * 0.6;
    float tl = uTime - a;
    vec4 c;
    bool open = cmdAt(j, tl, c) && N.w > 0.5;
    float apex;
    vec3 P = waterPos(j, c, tl, a, vec3(0.5), apex);
    vV = aSide;
    // a closed or sunk segment: collapsed on the nozzle, black, BEFORE any
    // lighting (an idle nozzle's 'water' sinks, exp(-y) overflowed and the
    // overflow spread over the strip's huge triangles: a white frame)
    if (!open || P.y < 0.0) { vC = vec3(0.0); gl_Position = clipOf(vec3(N.x, 0.0, N.y)); return; }
    vec3 P2 = waterPos(j, c, tl, a + 0.01, vec3(0.5), apex);
    vec4 p0 = clipOf(P), p1 = clipOf(P2);
    vec2 d = (p1.xy - p0.xy) * uPx;
    vec2 n = normalize(vec2(-d.y, d.x) + vec2(1e-6));
    // width: grows as the jet opens up; in px through the perspective
    float wWorld = 0.022 + 0.05 * a / 0.6;
    float wPx = max(1.5, uF * wWorld / max(P.z, 0.5) * (uPx.y / uImg.y) / uMap.y);
    float fade = (open && P.y >= 0.0) ? 1.0 : 0.0;
    float lit = 0.10 + 0.90 * exp(-max(P.y, 0.0) / (0.40 * max(c.z, 0.05)));
    // the body thins out past the apex (the water breaks into rain)
    // (an idle nozzle has apex 0: smoothstep with equal edges is undefined,
    // a NaN that whitened the whole frame)
    float ap = max(apex, 0.02);
    float body = 1.0 - smoothstep(ap * 0.85, ap * 1.4, a);
    vC = spotColour(j) * lit * body * fade * 0.55 * (1.0 + 1.2 * uFlash);
    gl_Position = vec4(p0.xy + n * aSide * wPx * 3.0 / uPx * 2.0, 0.0, 1.0);
  }
`;
const RIBBON_FRAGMENT = `
  precision mediump float;
  varying float vV;
  varying vec3 vC;
  void main() {
    // the half-width is 3 x the gaussian's sigma: 0 before the edge (R12)
    float v = vV * 3.0;
    float dens = exp(-0.5 * v * v);
    gl_FragColor = vec4(vC * (1.0 - exp(-dens * 1.8)), 1.0);
  }
`;

// MIST (tier >= 1) and the spots' pools on the water: soft sprites
const SPRITE_VERTEX = `
  precision highp float;
  ${JET_GLSL}
  attribute float aJet;
  attribute vec3 aSeed;
  attribute float aKind;          // 0 mist, 1 pool
  uniform float uMist;
  uniform vec2 uPx;
  uniform float uSpot;
  varying vec2 vLoc;
  varying vec3 vC;
  void main() {
    int j = int(aJet + 0.5);
    vec4 N = uNoz[j];
    vLoc = position.xy;
    if (N.w < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vC = vec3(0.0); return; }
    vec3 P;
    float rWorld, g;
    if (aKind < 0.5) {
      float life = fract(uTime * (0.05 + 0.04 * aSeed.x) + aSeed.z);
      P = vec3(N.x + sin(uTime * 0.2 + aSeed.y * 6.0) * 0.4 + life * 0.6, life * 1.6, N.y);
      rWorld = 0.18 + 0.30 * life;
      g = uMist * 0.010 * sin(3.14159 * life);
    } else {
      P = vec3(N.x, 0.0, N.y);
      rWorld = 0.11;
      g = uSpot * 0.12;
    }
    vC = spotColour(j) * g * (1.0 + 2.0 * uFlash);
    vec4 cp = clipOf(P);
    float rPx = uF * rWorld / P.z * (uPx.y / uImg.y) / uMap.y;
    // a pool lies flat on the water: squashed by the view angle
    vec2 r = aKind < 0.5 ? vec2(rPx) : vec2(rPx, rPx * 0.22);
    gl_Position = vec4(cp.xy + position.xy * r * 3.0 / uPx * 2.0, 0.0, 1.0);
  }
`;
const SPRITE_FRAGMENT = `
  precision mediump float;
  varying vec2 vLoc;
  varying vec3 vC;
  void main() {
    // sigma = 1/3 of the quad: 0 before the edge (R12)
    float r2 = dot(vLoc, vLoc) * 9.0;
    gl_FragColor = vec4(vC * exp(-0.5 * r2), 1.0);
  }
`;

// THE RIPPLES: the wave equation, R = height now, G = height before; fed
// where the water falls
const QUAD_VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const RIPPLE_FRAGMENT = `
  precision highp float;
  uniform sampler2D uPrev;
  uniform vec2 uTexel;
  uniform vec4 uImpact[${NJ}];      // X, Z, radius, strength
  uniform vec4 uDom;                // X0, X1, Z0, Z1
  varying vec2 vUv;
  void main() {
    vec4 c = texture2D(uPrev, vUv);
    float n = texture2D(uPrev, vUv + vec2(uTexel.x, 0.0)).r + texture2D(uPrev, vUv - vec2(uTexel.x, 0.0)).r
            + texture2D(uPrev, vUv + vec2(0.0, uTexel.y)).r + texture2D(uPrev, vUv - vec2(0.0, uTexel.y)).r;
    float h = (n * 0.5 - c.g) * 0.985;
    vec2 P = vec2(mix(uDom.x, uDom.y, vUv.x), mix(uDom.z, uDom.w, vUv.y));
    for (int i = 0; i < ${NJ}; i++) {
      vec4 I = uImpact[i];
      if (I.w > 0.0) {
        vec2 d = P - I.xy;
        h += I.w * exp(-dot(d, d) / (I.z * I.z));
      }
    }
    // the edges absorb
    vec2 e = min(vUv, 1.0 - vUv);
    h *= smoothstep(0.0, 0.04, min(e.x, e.y));
    gl_FragColor = vec4(clamp(h, -2.0, 2.0), c.r, 0.0, 1.0);
  }
`;

// THE PICTURE: the photo, the ripples, the jets, their reflection, the
// balustrade in front
const COMPOSITE_FRAGMENT = `
  precision highp float;
  uniform sampler2D uPhoto;
  uniform sampler2D uMask;
  uniform sampler2D uJets;
  uniform sampler2D uRipple;
  uniform vec4 uMap;
  uniform vec2 uImg;
  uniform float uYH;
  uniform float uF;
  uniform vec4 uDom;
  uniform vec2 uRipTex;
  uniform float uRipManual;
  uniform vec2 uLine;              // the nozzle line in the photo: y = a + b x
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform float uMaskView;
  uniform float uLakeLift;         // the drop's flash on the water
  varying vec2 vUv;
  float rip(vec2 uv) {
    if (uRipManual < 0.5) return texture2D(uRipple, uv).r;
    vec2 p = uv * uRipTex - 0.5, f = fract(p);
    vec2 b = (floor(p) + 0.5) / uRipTex, dx = vec2(1.0 / uRipTex.x, 0.0), dy = vec2(0.0, 1.0 / uRipTex.y);
    return mix(mix(texture2D(uRipple, b).r, texture2D(uRipple, b + dx).r, f.x),
               mix(texture2D(uRipple, b + dy).r, texture2D(uRipple, b + dx + dy).r, f.x), f.y);
  }
  vec2 imgToTex(vec2 ipx) { return vec2(ipx.x / uImg.x, 1.0 - ipx.y / uImg.y); }
  vec2 imgToScreen(vec2 ipx) { vec2 img = ipx / uImg; return vec2((img.x - 0.5) / uMap.x + 0.5, 1.0 - ((img.y - uMap.z) / uMap.y + 0.5)); }
  void main() {
    vec2 img = vec2((vUv.x - 0.5) * uMap.x + 0.5, ((1.0 - vUv.y) - 0.5) * uMap.y + uMap.z);
    vec2 ipx = img * uImg;
    vec4 m = texture2D(uMask, imgToTex(ipx));
    float lake = m.g, bal = m.b;
    // the ripples: the plane's height gradient displaces the reflection
    vec2 disp = vec2(0.0);
    float slope = 0.0;
    if (lake > 0.01 && ipx.y > uYH + 3.0) {
      float Z = uF / (ipx.y - uYH);
      float X = (ipx.x - uImg.x * 0.5) * Z / uF;
      vec2 ruv = vec2((X - uDom.x) / (uDom.y - uDom.x), (Z - uDom.z) / (uDom.w - uDom.z));
      if (ruv.x > 0.0 && ruv.x < 1.0 && ruv.y > 0.0 && ruv.y < 1.0) {
        vec2 t = 1.0 / uRipTex;
        vec2 gr = vec2(rip(ruv + vec2(t.x, 0.0)) - rip(ruv - vec2(t.x, 0.0)), rip(ruv + vec2(0.0, t.y)) - rip(ruv - vec2(0.0, t.y)));
        // in px, smaller with distance
        disp = gr * vec2(1.0, 2.2) * 9.0 * (uF / Z) / 120.0;
        slope = length(gr);
      }
    }
    vec3 photo = texture2D(uPhoto, imgToTex(ipx)).rgb;
    vec3 c = mix(photo, texture2D(uPhoto, imgToTex(ipx + disp)).rgb, lake);
    // the safe zone: the jets at half under the cover (floor 0.5)
    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float sa = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    vec3 jets = texture2D(uJets, vUv).rgb;
    // the reflection: mirrored about the nozzle line, blurred, broken by the ripples
    vec3 refl = vec3(0.0);
    float yl = uLine.x + uLine.y * ipx.x;
    if (lake > 0.01 && ipx.y > yl) {
      vec2 mp = vec2(ipx.x + disp.x * 1.5, 2.0 * yl - ipx.y + disp.y * 2.5);
      vec2 su = imgToScreen(mp);
      float bl = 3.0 / uImg.y / uMap.y;
      refl = (texture2D(uJets, su).rgb * 0.4 + texture2D(uJets, su + vec2(0.0, bl)).rgb * 0.3 + texture2D(uJets, su - vec2(0.0, bl)).rgb * 0.3);
      refl *= 0.42 * exp(-(ipx.y - yl) / 170.0) * (1.0 - 0.6 * clamp(slope * 6.0, 0.0, 1.0));
    }
    c += (jets + refl * lake) * sa;
    c += vec3(0.6, 0.7, 0.8) * uLakeLift * lake * 0.03;
    // the balustrade in front of everything
    c = mix(c, photo, bal);
    if (uMaskView > 0.5) c = mix(c, vec3(m.r, m.g, m.b), 0.6);
    gl_FragColor = vec4(c, 1.0);
  }
`;

// --- géométrie ------------------------------------------------------------------------
function dropGeometry(perJet) {
  const n = NJ * perJet;
  const jet = new Float32Array(n), seed = new Float32Array(n * 3), kind = new Float32Array(n), pos = new Float32Array(n * 3);
  let s = 0x1234567;
  const rnd = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  for (let i = 0; i < n; i++) {
    jet[i] = Math.floor(i / perJet);
    seed[i * 3] = rnd(); seed[i * 3 + 1] = rnd(); seed[i * 3 + 2] = rnd();
    kind[i] = (i % perJet) < perJet * 0.22 ? 1 : 0;             // a fifth are the curtain
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aJet', new THREE.BufferAttribute(jet, 1));
  g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 3));
  g.setAttribute('aKind', new THREE.BufferAttribute(kind, 1));
  return g;
}
function ribbonGeometry(M) {
  const nv = NJ * (M + 1) * 2;
  const pos = new Float32Array(nv * 3), jet = new Float32Array(nv), u = new Float32Array(nv), side = new Float32Array(nv);
  const idx = [];
  let v = 0;
  for (let j = 0; j < NJ; j++) {
    const base = v;
    for (let k = 0; k <= M; k++) for (const sd of [-1, 1]) { jet[v] = j; u[v] = k / M; side[v] = sd; v++; }
    for (let k = 0; k < M; k++) { const a = base + 2 * k; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aJet', new THREE.BufferAttribute(jet, 1));
  g.setAttribute('aU', new THREE.BufferAttribute(u, 1));
  g.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
  g.setIndex(idx);
  return g;
}
function spriteGeometry(mistPerJet) {
  const per = mistPerJet + 1;                      // the mist, then the pool
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  const n = NJ * per;
  const jet = new Float32Array(n), seed = new Float32Array(n * 3), kind = new Float32Array(n);
  let s = 0x7654321;
  const rnd = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  for (let i = 0; i < n; i++) { jet[i] = Math.floor(i / per); kind[i] = (i % per) === mistPerJet ? 1 : 0; seed[i * 3] = rnd(); seed[i * 3 + 1] = rnd(); seed[i * 3 + 2] = rnd(); }
  g.setAttribute('aJet', new THREE.InstancedBufferAttribute(jet, 1));
  g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 3));
  g.setAttribute('aKind', new THREE.InstancedBufferAttribute(kind, 1));
  g.instanceCount = n;
  return g;
}

// the spots' colours: three saturated palette colours (R4), hues 0.25 apart
function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }
export function spotColours(palette) {
  const hsl = { h: 0, s: 0, l: 0 }, cand = [];
  if (palette) for (const k of ['vividColors', 'dominantColors']) for (const c of (palette[k] || [])) { new THREE.Color(c).getHSL(hsl); cand.push({ c, h: hsl.h, s: hsl.s }); }
  cand.sort((a, b) => b.s - a.s);
  const out = [];
  for (const k of cand) { if (out.length >= 3) break; if (out.every((x) => hueDist(x.h, k.h) >= 0.25)) out.push(k); }
  const h0 = out.length ? out[0].h : 0.55;
  for (const dh of [0, 1 / 3, 2 / 3, 0.5]) { if (out.length >= 3) break; const h = (h0 + dh) % 1; if (out.every((x) => hueDist(x.h, h) >= 0.25)) out.push({ c: new THREE.Color().setHSL(h, 1, 0.5).getHex(), h }); }
  return out.map((x) => vdjColor(x.c, new THREE.Color(), 0.9));
}

export class FountainVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const f = (urlParam('fountainfig') || '').toLowerCase();
    this._forceFig = FIGS.includes(f) ? f : null;
    this._maskView = urlParam('fountainmask') === '1';
    this._post = { bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0.012, vignette: 0.25, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('fountainprobe') === '1' && typeof window !== 'undefined') window.__fountain = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    const A = getFountain();
    if (!A) { this._unavailable = true; return; }      // without its picture: unavailable
    const M = A.manifest;
    this._M = M;
    const [W, H] = M.size;
    // the nozzles, evenly spaced ON THE PLANE along the measured row
    const [n0, n1] = M.nozzles.plane;
    this._noz = [];
    const every = this._tier >= 1 ? 1 : 2;               // tier 0: 20 jets
    for (let j = 0; j < NJ; j++) {
      const t = j / (NJ - 1);
      const X = n0[0] + (n1[0] - n0[0]) * t, Z = n0[1] + (n1[1] - n0[1]) * t;
      this._noz.push({ X, Z, y: M.horizon + M.focal / Z, active: j % every === 0 });
    }
    // the nozzle line in the photo (for the mirror)
    const ya = this._noz[0], yb = this._noz[NJ - 1];
    const xa = W / 2 + M.focal * ya.X / ya.Z, xb = W / 2 + M.focal * yb.X / yb.Z;
    const lineB = (yb.y - ya.y) / (xb - xa), lineA = ya.y - lineB * xa;

    const v4s = (n) => Array.from({ length: n }, () => new THREE.Vector4());
    const cols = spotColours(null);
    this._u = {
      uJetA: { value: v4s(NJ) }, uJetB: { value: v4s(NJ) }, uNoz: { value: v4s(NJ) },
      uTime: { value: 0 }, uG: { value: 9.8 }, uSway: { value: new THREE.Vector4(0, 2, 0, 0) }, uFan: { value: 0 },
      uMap: { value: new THREE.Vector4(1, 1, 0.5, 0) }, uImg: { value: new THREE.Vector2(W, H) },
      uYH: { value: M.horizon }, uF: { value: M.focal }, uAspect: { value: 1.78 },
      uCol: { value: cols }, uColShift: { value: 0 }, uWarmMix: { value: 0 }, uFlash: { value: 0 },
      uCurtain: { value: 0.35 }, uCurtainH: { value: 0.25 }, uMist: { value: 0.4 }, uSpot: { value: 1 },
      uPx: { value: new THREE.Vector2(1920, 1080) }
    };
    for (let j = 0; j < NJ; j++) {
      const z = this._noz[j];
      this._u.uNoz.value[j].set(z.X, z.Z, j % 3, z.active ? 1 : 0);
      this._u.uJetA.value[j].set(-10, -9, 0, 0);
      this._u.uJetB.value[j].set(-10, -9, 0, 0);
    }
    // the jets' private scene, rendered into the jets' target
    this._jetScene = new THREE.Scene();
    this._cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const add = (geo, vs, fs, order, extra) => {
      const m = new THREE.Mesh(geo, new THREE.ShaderMaterial(Object.assign({
        uniforms: this._u, vertexShader: vs, fragmentShader: fs,
        transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
        // the strips turn with the jet: either winding must draw
        side: THREE.DoubleSide
      }, extra || {})));
      m.frustumCulled = false; m.renderOrder = order;
      this._jetScene.add(m);
      return m;
    };
    const sprites = add(spriteGeometry(this._tier >= 1 ? 3 : 0), SPRITE_VERTEX, SPRITE_FRAGMENT, 0);
    void sprites;
    add(ribbonGeometry(24), RIBBON_VERTEX, RIBBON_FRAGMENT, 1);
    const drops = new THREE.Points(dropGeometry(this._tier >= 2 ? 600 : (this._tier >= 1 ? 420 : 240)), new THREE.ShaderMaterial({
      uniforms: this._u, vertexShader: DROP_VERTEX, fragmentShader: DROP_FRAGMENT,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    }));
    drops.frustumCulled = false; drops.renderOrder = 2;
    this._jetScene.add(drops);
    this._jetRT = null;

    // the ripples: ping-pong 256 x 128
    this._ripU = {
      uPrev: { value: null }, uTexel: { value: new THREE.Vector2(1 / RIP.w, 1 / RIP.h) },
      uImpact: { value: v4s(NJ) }, uDom: { value: new THREE.Vector4(RIP.X0, RIP.X1, RIP.Z0, RIP.Z1) }
    };
    this._ripScene = new THREE.Scene();
    this._ripMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._ripU, vertexShader: QUAD_VERTEX, fragmentShader: RIPPLE_FRAGMENT, depthTest: false, depthWrite: false }));
    this._ripMesh.frustumCulled = false;
    this._ripScene.add(this._ripMesh);
    this._rip = null;

    // the picture, in the shared scene
    this._cU = {
      uPhoto: { value: A.photo }, uMask: { value: A.mask }, uJets: { value: null }, uRipple: { value: null },
      uMap: this._u.uMap, uImg: this._u.uImg, uYH: this._u.uYH, uF: this._u.uF,
      uDom: this._ripU.uDom, uRipTex: { value: new THREE.Vector2(RIP.w, RIP.h) }, uRipManual: { value: 0 },
      uLine: { value: new THREE.Vector2(lineA, lineB) }, uAspect: this._u.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather,
      uMaskView: { value: this._maskView ? 1 : 0 }, uLakeLift: { value: 0 }
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._cU, vertexShader: QUAD_VERTEX, fragmentShader: COMPOSITE_FRAGMENT, depthTest: false, depthWrite: false
    }));
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);

    // --- state ---
    this._time = 0; this._simAcc = 0;
    this._paused = false;
    this._clock = 0; this._beatIdx = 0; this._bpPrev = null; this._barPrev = null; this._phPrev = null;
    this._eighth = -1; this._barNo = 0; this._phraseNo = 0; this._beatNo = -1;
    this._fig = this._forceFig || 'sway'; this._figStart = 0;
    this._energy = 0.5;
    this._build = null; this._dropUntil = -1; this._flash = 0;
    this._modePrev = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined;
    this._waveIdx = 0;
    this._seed = 0xabcdef;
    this._log = [];
    this._barS = 4 * 60 / NO_BPM;
    this._afterDrop = false;
    this._startFigure(this._fig, 0);
  }

  _rand() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(what, extra) { this._log.push(Object.assign({ t: +this._time.toFixed(2), what }, extra || {})); if (this._log.length > 300) this._log.shift(); }

  // the highest a nozzle's jet may go: 60 % of the screen at the nearest
  // nozzle, never into the top band
  _maxH(j) {
    const z = this._noz[j], M = this._M;
    const sy = this._u.uMap.value.y, cy = this._u.uMap.value.z, Himg = M.size[1];
    const screenPx = sy * Himg;                                  // the screen's height, in photo px
    const near = this._noz[0];
    const hNear = 0.60 * screenPx * near.Z / M.focal;            // world height: 60 % at the nearest
    const topY = (cy - sy / 2 + TOP_BAND * sy) * Himg;           // the band's lower edge, photo px
    const cap = Math.max(0, (z.y - topY) * z.Z / M.focal);
    return Math.min(hNear, cap);
  }
  _cmd(j, tOn, tOff, H, kind) {
    const A = this._u.uJetA.value[j], B = this._u.uJetB.value[j];
    B.copy(A);
    A.set(tOn, tOff, Math.min(H, this._maxH(j)), kind);
  }
  _startFigure(fig, now) {
    this._fig = fig; this._figStart = this._clock; this._waveIdx = 0;
    const S = this._u.uSway.value;
    // oarsmen run continuously for SWAY and CROSS; everything stops otherwise
    for (let j = 0; j < NJ; j++) {
      if (fig === 'sway' || fig === 'cross') this._cmd(j, now, 1e9, this._maxH(j) * 0.55, 1);
      else this._cmd(j, now, now, 0, 0);
    }
    if (fig === 'sway') { S.set(22 * Math.PI / 180, this._barS, now, 0); this._u.uFan.value = 24 * Math.PI / 180; }
    if (fig === 'cross') { S.set(16 * Math.PI / 180, this._barS, now, 1); this._u.uFan.value = 22 * Math.PI / 180; }
    this._note('figure', { fig });
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _updateMap() {
    // cover-fit of the photo; on wider screens keep the lake and the
    // balustrade (the crop's centre at 62 % of the height)
    const A = safeZoneUniforms.uAspect.value || 1.78;
    const [W, H] = this._M.size, Ai = W / H;
    let sx = 1, sy = 1, cy = 0.5;
    if (A > Ai) { sy = Ai / A; cy = Math.min(1 - sy / 2, Math.max(sy / 2, 0.62)); }
    else sx = A / Ai;
    this._u.uMap.value.set(sx, sy, cy, 0);
    this._u.uAspect.value = A;
  }

  update(deltaTime, profile) {
    if (this._unavailable) return;
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._updateMap();
    if (profile.artworkPalette !== this._paletteSeen) {
      this._paletteSeen = profile.artworkPalette;
      const cols = spotColours(profile.artworkPalette);
      for (let i = 0; i < 3; i++) this._u.uCol.value[i].copy(cols[i]);
    }
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;             // frozen: the water stops in the air
    this._time += dt;
    this._simAcc += dt;
    const now = this._time;
    this._u.uTime.value = now;

    // THE CLOCK
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const grid = bp != null && bpm > 0;
    const tempo = grid ? bpm : NO_BPM;
    const beatS = 60 / tempo;
    this._barS = 4 * beatS;
    let barEdge = false, phraseEdge = false, beatEdge = false;
    if (grid) {
      if (this._bpPrev != null && bp < this._bpPrev - 0.5) { this._beatIdx++; beatEdge = true; }
      this._bpPrev = bp;
      this._clock = this._beatIdx + bp;
      if (barIn != null) { if (this._barPrev != null && barIn < this._barPrev - 0.5) barEdge = true; this._barPrev = barIn; }
      if (ph != null) { if (this._phPrev != null && ph < this._phPrev - 0.5) phraseEdge = true; this._phPrev = ph; }
    } else {
      const prev = this._clock;
      this._clock += dt / beatS;
      if (Math.floor(this._clock) !== Math.floor(prev)) {
        beatEdge = true;
        const b = Math.floor(this._clock);
        if (b % 4 === 0) barEdge = true;
        if (b % 32 === 0) phraseEdge = true;
      }
      this._bpPrev = null; this._barPrev = null; this._phPrev = null;
    }
    const e8 = Math.floor(this._clock * 2), eighthEdge = e8 !== this._eighth;
    this._eighth = e8;
    if (barEdge) { this._barNo++; this._u.uColShift.value = (this._u.uColShift.value + 1) % 3; }
    const beatInBar = grid && barIn != null ? Math.floor(barIn * 4 + 1e-4) : (Math.floor(this._clock) % 4);

    // THE ENERGY of the segment (smoothed over 2 bars), the structure
    const dir = profile.director || {};
    const e = dir.intensity != null ? clamp01(dir.intensity) : 0.5;
    this._energy += (e - this._energy) * Math.min(1, dt / (2 * this._barS));
    const mode = dir.mode || null;
    const drop = (grid && this._tickDrops(profile)) || (mode === 'drop' && this._modePrev != null && this._modePrev !== 'drop');
    if (mode === 'build' && this._modePrev !== 'build') this._build = { start: this._clock };
    if (mode !== 'build') this._build = null;
    this._modePrev = mode;
    const buildProg = this._build ? clamp01((this._clock - this._build.start) / 32) : 0;
    const hMul = (0.55 + 0.45 * this._energy) * (this._build ? 0.75 + 0.5 * buildProg : 1);

    // THE PHRASE: the next figure, by the energy
    if (phraseEdge && now > this._dropUntil) {
      this._phraseNo++;
      let fig = this._forceFig;
      if (!fig) {
        const k = this._phraseNo % 2;
        fig = this._energy < 0.35 ? 'breath' : (this._energy < 0.6 ? (k ? 'sway' : 'cross') : (k ? 'wave' : 'pulse'));
      }
      this._startFigure(fig, now);
    }

    // THE DROP: every shooter at the top together, a white flash, rain
    if (drop) {
      for (let j = 0; j < NJ; j++) this._cmd(j, now, now + RISE_S, this._maxH(j), 0);
      this._flash = 1;
      this._dropUntil = now + 2 * beatS + RISE_S;
      this._afterDrop = true;
      this._note('drop');
    }
    this._flash = Math.max(0, this._flash - dt / 0.45);
    this._u.uFlash.value = this._flash;
    this._cU.uLakeLift.value = this._flash;

    // after the drop's rain, the figure picks up again
    if (this._afterDrop && now > this._dropUntil) { this._afterDrop = false; this._startFigure(this._fig, now); }
    // THE FIGURE, on the grid (not during the drop's rain)
    if (now > this._dropUntil) {
      const f = this._fig;
      if (f === 'wave' && eighthEdge) {
        // one shooter per eighth, left to right
        const j = this._waveIdx % NJ;
        this._cmd(j, now, now + 0.55 * beatS, this._maxH(j) * hMul, 0);
        // tier 0 skips the inactive nozzles: keep the wave's pace
        this._waveIdx += this._noz[(j + 1) % NJ].active ? 1 : 2;
      } else if (f === 'pulse' && beatEdge && (beatInBar === 0 || beatInBar === 2)) {
        for (let j = 0; j < NJ; j++) this._cmd(j, now, now + 0.45 * beatS, this._maxH(j) * hMul, 0);
      } else if ((f === 'sway' || f === 'cross') && barEdge) {
        // the oarsmen follow the energy each bar (height), the sway keeps one swing per bar
        for (let j = 0; j < NJ; j++) {
          const A = this._u.uJetA.value[j];
          if (A.w > 0.5) A.z = Math.min(this._maxH(j) * 0.55 * hMul, this._maxH(j));
        }
        this._u.uSway.value.y = this._barS;
      }
    }
    // curtain and mist: BREATH is theirs; the build thickens the mist
    const breath = this._fig === 'breath' && now > this._dropUntil;
    this._u.uCurtain.value = breath ? 1 : 0.35;
    this._u.uCurtainH.value = this._maxH(0) * (breath ? 0.18 : 0.10);
    this._u.uMist.value = Math.min(1.6, (breath ? 1 : 0.4) + 0.8 * buildProg);
    this._u.uWarmMix.value = breath ? 0.6 : 0;
    // gravity: the highest jet rises in 0.3 s
    const hTop = this._maxH(0);
    this._u.uG.value = 2 * Math.max(hTop, 0.1) / (RISE_S * RISE_S);

    // the water's impacts on the lake: where each jet falls
    const g = this._u.uG.value;
    const S = this._u.uSway.value;
    for (let j = 0; j < NJ; j++) {
      const A = this._u.uJetA.value[j], I = this._ripU.uImpact.value[j], z = this._noz[j];
      const open = now >= A.x && now < A.y + 0.6 && A.z > 0.01 && z.active;
      if (!open) { I.set(0, 0, 1, 0); continue; }
      const v0 = Math.sqrt(2 * g * A.z);
      let th = 0;
      if (A.w > 0.5) {
        const u = j / (NJ - 1) * 2 - 1, ee = j % 2 === 0 ? 1 : -1;
        th = (S.w < 0.5 ? u * this._u.uFan.value : ee * this._u.uFan.value) + S.x * Math.sin(2 * Math.PI * (now - S.z) / S.y) * (S.w < 0.5 ? 1 : ee);
      }
      const range = v0 * v0 * Math.sin(2 * th) / g;
      const jitter = (this._rand() - 0.5) * 0.6;
      I.set(z.X + range + jitter * Math.sqrt(A.z), z.Z + (this._rand() - 0.5) * 0.4, 0.18 + 0.15 * Math.sqrt(A.z), 0.25 * Math.min(1.5, A.z));
    }
  }

  _ensureTargets(renderer, w, h) {
    const ext = renderer.extensions;
    const floatOk = renderer.capabilities.isWebGL2 && (ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float'));
    if (!this._rip) {
      const mk = () => new THREE.WebGLRenderTarget(RIP.w, RIP.h, {
        type: floatOk ? THREE.HalfFloatType : THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping
      });
      this._rip = { a: mk(), b: mk() };
      this._cU.uRipManual.value = floatOk ? 1 : 0;
      const prev = renderer.getRenderTarget(), cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
      renderer.setClearColor(0x000000, 0);
      for (const t of [this._rip.a, this._rip.b]) { renderer.setRenderTarget(t); renderer.clear(true, false, false); }
      renderer.setRenderTarget(prev); renderer.setClearColor(cc, ca);
    }
    if (!this._jetRT || this._jetRT.width !== w || this._jetRT.height !== h) {
      if (this._jetRT) this._jetRT.dispose();
      this._jetRT = new THREE.WebGLRenderTarget(w, h, {
        type: floatOk ? THREE.HalfFloatType : THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false
      });
      // the Pi cannot filter float: nearest then (the reflection's taps blur it)
      if (floatOk) { this._jetRT.texture.minFilter = THREE.NearestFilter; this._jetRT.texture.magFilter = THREE.NearestFilter; }
    }
  }

  _render(renderer) {
    if (this._unavailable) return;
    const cur = renderer.getRenderTarget();
    const size = cur ? _size.set(cur.width, cur.height) : renderer.getDrawingBufferSize(_size);
    const w = Math.max(2, Math.round(size.x)), h = Math.max(2, Math.round(size.y));
    this._ensureTargets(renderer, w, h);
    this._u.uPx.value.set(w, h);
    const autoClear = renderer.autoClear, cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    renderer.autoClear = false;
    if (!this._paused) {
      // the ripples at 30 Hz, whatever the frame rate (2 steps at most)
      let steps = 0;
      while (this._simAcc >= 1 / 30 && steps < 2) {
        this._ripU.uPrev.value = this._rip.a.texture;
        renderer.setRenderTarget(this._rip.b);
        renderer.render(this._ripScene, this._cam);
        const t = this._rip.a; this._rip.a = this._rip.b; this._rip.b = t;
        this._simAcc -= 1 / 30; steps++;
      }
      if (steps === 2) this._simAcc = 0;
    }
    renderer.setRenderTarget(this._jetRT);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, false, false);
    renderer.render(this._jetScene, this._cam);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.autoClear = autoClear;
    this._cU.uJets.value = this._jetRT.texture;
    this._cU.uRipple.value = this._rip.a.texture;
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    if (this._jetScene) for (const m of this._jetScene.children.slice()) { this._jetScene.remove(m); m.geometry.dispose(); m.material.dispose(); }
    if (this._ripMesh) { this._ripMesh.geometry.dispose(); this._ripMesh.material.dispose(); }
    if (this._jetRT) this._jetRT.dispose();
    if (this._rip) { this._rip.a.dispose(); this._rip.b.dispose(); }
    if (typeof window !== 'undefined' && window.__fountain === this) window.__fountain = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
